import { apiClient } from './client'

// ==================== TYPES ====================

export interface Grade {
  _id: string
  id: string
  name: string
  description?: string
  isActive: boolean
  streamCount: number
  studentCount?: number // Total students across all streams in this grade
  streams?: Stream[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface Stream {
  _id: string
  id: string
  name: string
  gradeId?: string
  gradeName?: string
  classTeacher?: string
  classroom?: string // TODO: Will be added when classroom assignment is implemented
  description?: string
  isActive: boolean
  lessonCount?: number
  lessonsCount?: number // Alias for lessonCount for backward compatibility
  studentCount?: number // Number of students in this stream
  currentStudents?: number // Alias for studentCount for backward compatibility
  capacity?: number // TODO: Will be added when student assignment is implemented
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface Lesson {
  id: string
  title: string
  subject: string
  description?: string
  duration: number
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced'
  objectives?: string
  materials?: string
  streams: {
    id: string
    name: string
    gradeName: string
    classTeacher?: string
  }[]
  teacherIds?: string[]
  teachers?: {
    id: string
    name: string
  }[]
  isActive: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface CreateGradeRequest {
  name: string
  description?: string
}

export interface CreateStreamRequest {
  name: string
  gradeId: string
  classTeacher?: string
  description?: string
}

export interface CreateLessonRequest {
  title: string
  subject: string
  description?: string
  duration: number
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced'
  objectives?: string
  materials?: string
  streamIds: string[]
}

export interface UpdateGradeRequest {
  name?: string
  description?: string
  isActive?: boolean
}

export interface UpdateStreamRequest {
  name?: string
  classTeacher?: string
  description?: string
  isActive?: boolean
}

export interface UpdateLessonRequest {
  title?: string
  subject?: string
  description?: string
  duration?: number
  difficulty?: 'Beginner' | 'Intermediate' | 'Advanced'
  objectives?: string
  materials?: string
  streamIds?: string[]
  teacherIds?: string[]
  isActive?: boolean
}

// ==================== GRADES API ====================

// Get all grades with pagination and search
export const getGrades = async (params?: {
  page?: number
  limit?: number
  search?: string
  status?: 'active' | 'inactive' | 'all'
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}): Promise<{
  grades: Grade[]
  pagination: {
    currentPage: number
    totalPages: number
    totalGrades: number
    limit: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}> => {
  const searchParams = new URLSearchParams()
  
  if (params?.page) searchParams.append('page', params.page.toString())
  if (params?.limit) searchParams.append('limit', params.limit.toString())
  if (params?.search) searchParams.append('search', params.search)
  if (params?.status && params.status !== 'all') searchParams.append('status', params.status)
  if (params?.sortBy) searchParams.append('sortBy', params.sortBy)
  if (params?.sortOrder) searchParams.append('sortOrder', params.sortOrder)
  
  const url = searchParams.toString() ? `/academic/grades?${searchParams}` : '/academic/grades'
  const response = await apiClient.get(url)
  return response.data.data
}

// Get single grade by ID
export const getGradeById = async (gradeId: string): Promise<Grade> => {
  const response = await apiClient.get(`/academic/grades/${gradeId}`)
  return response.data.data
}

// Create new grade
export const createGrade = async (gradeData: CreateGradeRequest): Promise<Grade> => {
  const response = await apiClient.post('/academic/grades', gradeData)
  return response.data.data
}

// Update grade
export const updateGrade = async (gradeId: string, gradeData: UpdateGradeRequest): Promise<Grade> => {
  const response = await apiClient.put(`/academic/grades/${gradeId}`, gradeData)
  return response.data.data
}

// Delete grade
export const deleteGrade = async (gradeId: string): Promise<{ id: string, name: string }> => {
  const response = await apiClient.delete(`/academic/grades/${gradeId}`)
  return response.data.data
}

// Get grade statistics
export const getGradeStats = async (): Promise<{
  totalGrades: number
  activeGrades: number
  inactiveGrades: number
  totalStreams: number
}> => {
  const response = await apiClient.get('/academic/grades/stats')
  return response.data.data
}

// ==================== STREAMS API ====================

// Get all streams (optionally filter by grade, institution, search, and status)
export const getStreams = async (params: {
  gradeId?: string
  institutionId?: string
  search?: string
  status?: 'active' | 'inactive' | 'all'
} = {}): Promise<Stream[]> => {
  const searchParams = new URLSearchParams()

  if (params.gradeId) searchParams.append('gradeId', params.gradeId)
  if (params.institutionId) searchParams.append('institutionId', params.institutionId)
  if (params.search) searchParams.append('search', params.search)
  if (params.status && params.status !== 'all') searchParams.append('status', params.status)

  const url = searchParams.toString() ? `/academic/streams?${searchParams.toString()}` : '/academic/streams'
  const response = await apiClient.get(url)
  return response.data.data
}

// Get single stream by ID
export const getStreamById = async (streamId: string): Promise<Stream> => {
  const response = await apiClient.get(`/academic/streams/${streamId}`)
  return response.data.data
}

// Create new stream
export const createStream = async (streamData: CreateStreamRequest): Promise<Stream> => {
  const response = await apiClient.post('/academic/streams', streamData)
  return response.data.data
}

// Update stream
export const updateStream = async (streamId: string, streamData: UpdateStreamRequest): Promise<Stream> => {
  const response = await apiClient.put(`/academic/streams/${streamId}`, streamData)
  return response.data.data
}

// Delete stream
export const deleteStream = async (streamId: string): Promise<{ id: string, name: string }> => {
  const response = await apiClient.delete(`/academic/streams/${streamId}`)
  return response.data.data
}

// Get stream statistics
export const getStreamStats = async (): Promise<{
  totalStreams: number
  activeStreams: number
  inactiveStreams: number
  byGrade: Record<string, number>
}> => {
  const response = await apiClient.get('/academic/streams/stats')
  return response.data.data
}

// ==================== LESSONS API ====================

// Get all lessons (with optional filters)
export const getLessons = async (params?: {
  subject?: string
  difficulty?: string
  streamId?: string
  gradeId?: string
}): Promise<Lesson[]> => {
  const searchParams = new URLSearchParams()
  if (params?.subject) searchParams.append('subject', params.subject)
  if (params?.difficulty) searchParams.append('difficulty', params.difficulty)
  if (params?.streamId) searchParams.append('streamId', params.streamId)
  if (params?.gradeId) searchParams.append('gradeId', params.gradeId)

  const url = searchParams.toString() ? `/academic/lessons?${searchParams}` : '/academic/lessons'
  const response = await apiClient.get(url)
  return response.data.data
}

// Get single lesson by ID
export const getLessonById = async (lessonId: string): Promise<Lesson> => {
  const response = await apiClient.get(`/academic/lessons/${lessonId}`)
  return response.data.data
}

// Create new lesson
export const createLesson = async (lessonData: CreateLessonRequest): Promise<Lesson> => {
  const response = await apiClient.post('/academic/lessons', lessonData)
  return response.data.data
}

// Update lesson
export const updateLesson = async (lessonId: string, lessonData: UpdateLessonRequest): Promise<Lesson> => {
  const response = await apiClient.put(`/academic/lessons/${lessonId}`, lessonData)
  return response.data.data
}

// Delete lesson
export const deleteLesson = async (lessonId: string): Promise<{ id: string, title: string }> => {
  const response = await apiClient.delete(`/academic/lessons/${lessonId}`)
  return response.data.data
}

// Get lesson statistics
export const getLessonStats = async (): Promise<{
  totalLessons: number
  activeLessons: number
  inactiveLessons: number
  bySubject: Record<string, number>
  byDifficulty: Record<string, number>
}> => {
  const response = await apiClient.get('/academic/lessons/stats')
  return response.data.data
}